import { ScoreCard, TrajectoryChart, WhiteBox } from "@foodpilot/foods";
import { Stack, useTheme, useMediaQuery } from "@mui/material";
import { useTranslation } from "react-i18next";
import { ActionPlanSsqPostSummary } from "src/api/client-api/ActionPlan/ActionPlan";
import { Loading } from "src/components/utils/Loading";
import { useActionsContext } from "src/context/Actions/useActionsContext";
import { NoGoalDefined } from "./NoGoalDefined";
import { useApplicationParameters } from "src/api/client-api/appParams";
import { useEntityScores } from "src/context/Scores/useEntityScores";
import {
    mapVariationsToScoreCardOptions,
    useActionPlanSelectorVariations,
} from "src/hooks/useActionPlanSelectorVariations";
import { Suspense } from "src/components/utils/Suspense";
import { OlapScoresService } from "src/context/OlapScores/OlapScoresService";
import { EmptyCard } from "src/components/Actions/Tabs/2_TabActionPlan/ActionsPreview/EmptyCard";
import { getMeasuredValueByCampaignId, splitMeasuredYears } from "./measuredTrajectory";

export const TrajectoriesBarChart = () => {
    const { actionPlan, actionPlanData, persistedOptions, isFetched, companyPosts } = useActionsContext();
    const { lastSelectedVariation, scoresWithVariations } = useEntityScores();
    const selectorVariations = useActionPlanSelectorVariations(scoresWithVariations);
    const { applicationParameters } = useApplicationParameters();
    const { t } = useTranslation();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("md"));

    if (actionPlan === undefined || actionPlan === null) {
        return <Loading />;
    }

    const startSsqPost = actionPlan.startSsqPost;
    const actionPlanSsqPosts = actionPlan.actionPlanSsqPosts;
    const simulationSsqPosts = actionPlan.simulationSsqPosts;

    const selectedScoreId = lastSelectedVariation.scoreId;
    const startScore = startSsqPost.olapScores[selectedScoreId];

    const isActionPlanSimplified = applicationParameters?.["actionPlan-mode"] === "simplified";

    const variationsOptions = mapVariationsToScoreCardOptions(
        selectorVariations.filter((scoreWithVariation) => {
            return actionPlanData.some(
                (item) =>
                    item.scoreId === scoreWithVariation.scoreId && item.variationId === scoreWithVariation.variationId,
            );
        }),
    );

    const actionPlanScores = actionPlanData.find((actionPlan) => {
        const doesScoreMatch = actionPlan.scoreId === lastSelectedVariation.scoreId;
        const doesVariationMatch = actionPlan.variationId === lastSelectedVariation.variationId;
        return doesScoreMatch && doesVariationMatch;
    });

    if (actionPlanScores === undefined) {
        return <NoGoalDefined />;
    }

    const startValue = OlapScoresService.normalizeNumericalScore(startScore?.score.value);
    const hasStartValue = startValue !== null;

    const measuredValueByCampaignId = getMeasuredValueByCampaignId(
        companyPosts,
        selectedScoreId,
        startSsqPost.campaign?.id,
    );
    const { measuredBars, projectedPosts } = splitMeasuredYears(simulationSsqPosts, measuredValueByCampaignId);

    const startingData =
        hasStartValue ?
            [
                {
                    label: startSsqPost.campaign?.label ?? "",
                    value: startValue,
                },
                ...measuredBars,
            ]
        :   [];

    const trajectoryData = projectedPosts.map((simulationPost: ActionPlanSsqPostSummary) => {
        const score = simulationPost.olapScores[selectedScoreId]?.score.value ?? null;

        if (isActionPlanSimplified) {
            return {
                label: simulationPost.campaign?.label ?? "",
                values: [null, score],
            };
        }

        const postWithoutActionPlan = actionPlanSsqPosts.find((post) => {
            return (
                post.ssqPost.campaign &&
                simulationPost.campaign &&
                post.ssqPost.campaign.id === simulationPost.campaign.id
            );
        });

        const scoreWithoutActionPlan = postWithoutActionPlan?.ssqPost.olapScores[selectedScoreId]?.score.value ?? null;

        return {
            label: simulationPost.campaign?.label ?? "",
            values: [scoreWithoutActionPlan, score],
        };
    }) as any; // idc about any right there, foods upgrade killed typesafety here

    const trajectoryGoal = actionPlanScores.metadata.goal?.value;

    return (
        <WhiteBox
            sx={{
                ...(isMobile && {
                    border: "none",
                }),
            }}
        >
            <Stack>
                <ScoreCard
                    header={{
                        selectedItemId: lastSelectedVariation.rowId,
                        options: variationsOptions,
                        variant: "secondary",
                        orientation: isMobile ? "vertical" : "horizontal",
                    }}
                    disableFooter={true}
                >
                    <Suspense isLoading={!isFetched}>
                        {!hasStartValue ?
                            <EmptyCard
                                title={t("action_plan.preview.no_comparison_data", "No comparison data")}
                                subtitle={t(
                                    "action_plan.preview.no_comparison_data.description",
                                    "The API returned no comparison data for this action plan and score.",
                                )}
                            />
                        :   <TrajectoryChart
                                goal={trajectoryGoal}
                                unit={lastSelectedVariation.unit}
                                height="600px"
                                initialValues={startingData}
                                improvement={lastSelectedVariation.improvement}
                                trajectory={trajectoryData}
                                trajectoryOptions={{
                                    initialValues: { label: t("trajectory_chart.calculatedscore") },
                                    target: { label: t("trajectory_chart.goal"), color: "#3A86FF" },
                                    main: { label: persistedOptions.withoutPlanName },
                                    difference: { label: persistedOptions.withPlanName },
                                }}
                                textOptions={{
                                    complete: t("trajectory_chart.complete"),
                                    between: t("trajectory_chart.between"),
                                    and: t("trajectory_chart.and"),
                                    withoutActionPlan: persistedOptions.comparedToWithoutPlan,
                                    goal: t("trajectory_chart.goal"),
                                    noObjective: t("trajectory_chart.noobjective"),

                                    // Whenever we implement the opening of a modal with a link
                                    // We can use this parameter
                                    // editTrajectories: t("trajectory_chart.edittrajectories"),
                                }}
                            />
                        }
                    </Suspense>
                </ScoreCard>
            </Stack>
        </WhiteBox>
    );
};
